import type { Brand } from "./brand";
import type { Category } from "./category";
import type { Country } from "./country";
import type { ProductType, ValuesOf } from "./enums";
import { id } from "./id";
import type { Tag } from "./tag";

interface product {
    id: string;
    categoryId: number;
    brandId: number;
    productId: number;

    gtin?: string;
    mpn?: string;
    name: string;
    variant?: string;
    released?: string;
    type: ValuesOf<typeof ProductType>;
    rating?: number;
    image?: string;
    origin?: ValuesOf<typeof Country>;
    color?: [number, number, number];
    q: string;
}

export interface Product extends product {
    category: string;
    categoryImage?: string;
    brand: string;
    brandImage?: string;
    tag: Tag[];
    stock: number;
    images: product_image[];
}

interface product_image {
    id: string;
    accountId: number;
    brandId: number;
    productId: number;
    imageId: number;
    path: string;
}

export const productToBrand = (x: Product): Brand => ({ 
    id: id(x.brandId),
    brandId: x.brandId!, 
    name: x.brand!,
    image: x.brandImage
}) as Brand;

export const productToCategory = (x: Product): Category => ({ 
    id: id(x.categoryId),
    categoryId: x.categoryId!, 
    name: x.category!,
    image: x.categoryImage
}) as Category;

export const fullName = (x: { name: string, variant?: string }) => x.variant ? `${x.name} | ${x.variant}` : x.name;

export const productFullName = (record: { brand: string, name: string, variant?: string }, includeBrand = true) => includeBrand ? `${record.brand} ${fullName(record)}` : fullName(record);

export const transformProduct = (accountId: number) => (record: Product) => {
    const { category, categoryImage, brandId, brand, brandImage, stock, ...r } = record;
    const product: Product = Number.isNaN(Number.parseInt(brandId as unknown as string)) ? ({ brand: brandId, ...r } as any) : { brandId, ...r };
    const tag = r.tag ?? [];
    const images = r.images ?? [];
    return {
        ...product,
        tag: tag
            .map((t: Tag) => ({
                accountId,
                categoryId: product.categoryId,
                specificationId: t.specificationId,
                value: t.unit ?? t.value,
                unit: t.unit,
            }))
            .filter(x => !!x.value),
        images: images.map((x, i) => ({ ...x, accountId, imageId: i + 1 })),
    };
};